import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  FiUsers, FiCalendar, FiInstagram, FiVideo, FiImage,
  FiGift, FiDollarSign, FiCheck, FiX, FiArrowRight, FiFileText
} from 'react-icons/fi';
import { FaTiktok, FaFacebook } from 'react-icons/fa';
import { campaignsApi, applicationsApi, usersApi, getImageUrl } from '../utils/api';
import { trackViewContent, trackSubmitApplication } from '../utils/metaPixel';
import { useAuth } from '../context/AuthContext';
import AgreementModal from '../components/AgreementModal';
import './CampaignDetailPage.css';

const CampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  const { data: campaignData, isLoading, error, refetch, isFetching } = useQuery(
    ['campaign', id],
    () => campaignsApi.getById(id),
    {
      // A 404 is final — don't retry it. Transient errors (429/5xx/network) get
      // a few automatic retries before we fall back to the manual retry button.
      retry: (failureCount, err) =>
        err?.response?.status !== 404 && failureCount < 3,
      onSuccess: (data) => {
        const c = data?.data?.campaign;
        if (c) trackViewContent({ content_name: c.title, content_ids: [id], content_type: 'campaign' });
      }
    }
  );

  // Check if current user already applied to this campaign
  const { data: myApplicationsData } = useQuery(
    ['my-applications-check'],
    () => applicationsApi.getMy({ limit: 100 }),
    { enabled: !!user }
  );

  const hasAlreadyApplied = myApplicationsData?.data?.applications?.some(
    app => app.campaign_id === id
  );

  const existingApplication = myApplicationsData?.data?.applications?.find(
    app => app.campaign_id === id
  );

  // Mutation for signing agreement
  const signAgreementMutation = useMutation(
    (data) => usersApi.signAgreement(data),
    {
      onSuccess: () => {
        toast.success('ההסכם נחתם בהצלחה! כעת תוכל להגיש מועמדות לקמפיינים.');
        setShowAgreementModal(false);
        refreshUser();
        // Now apply to the campaign
        applyMutation.mutate({ message: 'הגשה לאחר חתימת הסכם' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'שגיאה בחתימת ההסכם');
      }
    }
  );

  const applyMutation = useMutation(
    (data) => applicationsApi.apply(id, data),
    {
      onSuccess: () => {
        trackSubmitApplication({ content_name: campaign?.title, content_ids: [id] });
        toast.success('הבקשה נשלחה בהצלחה!');
        queryClient.invalidateQueries(['campaign', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'שגיאה בשליחת הבקשה');
      }
    }
  );

  const campaign = campaignData?.data?.campaign;

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'instagram': return FiInstagram;
      case 'tiktok': return FaTiktok;
      case 'facebook': return FaFacebook;
      default: return FiInstagram;
    }
  };

  const handleApply = () => {
    if (!user) {
      navigate('/creator-login');
      return;
    }

    // Check if user has signed agreement
    if (user.agreementSigned) {
      // User already signed, apply directly
      applyMutation.mutate({ message: 'הגשת מועמדות' });
    } else {
      // Show agreement modal
      setShowAgreementModal(true);
    }
  };

  const handleAgreementSubmit = (agreementData) => {
    signAgreementMutation.mutate(agreementData);
  };

  if (isLoading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !campaign) {
    // Distinguish a real 404 (campaign deleted/never existed) from a transient
    // failure (rate limit / network / server hiccup) so a momentary glitch isn't
    // shown as "campaign not found" with no way to recover.
    const isNotFound = error?.response?.status === 404 || (!error && !campaign);

    if (isNotFound) {
      return (
        <div className="error-page container py-16 text-center">
          <h2>הקמפיין לא נמצא</h2>
          <button className="btn btn-primary mt-4" onClick={() => navigate('/campaigns')}>
            חזרה לקמפיינים
          </button>
        </div>
      );
    }

    return (
      <div className="error-page container py-16 text-center">
        <h2>שגיאה זמנית בטעינת הקמפיין</h2>
        <p>ייתכן שיש עומס רגעי. אפשר לנסות שוב.</p>
        <div className="mt-4">
          <button className="btn btn-primary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'טוען...' : 'נסה שוב'}
          </button>
          <button className="btn btn-secondary mt-4" onClick={() => navigate('/campaigns')}>
            חזרה לקמפיינים
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-detail-page">
      {/* Header */}
      <section className="campaign-header">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/campaigns')}>
            <FiArrowRight />
            חזרה לקמפיינים
          </button>

          <div className="header-content">
            <div className="header-info">
              <div className="brand-info">
                {campaign.brandLogo && (
                  <img src={campaign.brandLogo} alt={campaign.brandName} className="brand-logo" />
                )}
                <span className="brand-name">{campaign.brandName}</span>
              </div>
              <h1>{campaign.title}</h1>
              <div className="campaign-meta">
                {campaign.isOpenForApplications ? (
                  <span className="badge badge-success">הרשמה פעילה</span>
                ) : (
                  <span className="badge badge-error">הרשמה סגורה</span>
                )}
                {campaign.daysRemaining != null && (
                  <span className="meta-item">
                    <FiCalendar />
                    {campaign.daysRemaining} ימים להגשה
                  </span>
                )}
                <span className="meta-item">
                  <FiUsers />
                  {campaign.applicationsCount || 0} הגשות
                </span>
              </div>
            </div>

            {campaign.coverImage && (
              <div className="header-image">
                <img src={getImageUrl(campaign.coverImage)} alt={campaign.title} />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="campaign-content section">
        <div className="container">
          <div className="content-grid">
            {/* Main Content */}
            <div className="main-content">
              <div className="content-card">
                <h2>אודות הקמפיין</h2>
                {campaign.shortDescription && (
                  <p className="campaign-short-description">{campaign.shortDescription}</p>
                )}
                <p className="campaign-description">{campaign.description}</p>
              </div>

              {/* Requirements */}
              <div className="content-card">
                <h2>דרישות</h2>
                <div className="requirements-list">
                  {campaign.requirements?.map((req, i) => (
                    <div key={i} className="requirement-item">
                      {req.type === 'reel' && <FiVideo className="req-icon" />}
                      {req.type === 'story' && <FiImage className="req-icon" />}
                      {req.type === 'post' && <FiImage className="req-icon" />}
                      <div className="req-info">
                        <strong>{req.quantity}x {req.type === 'reel' ? 'ריל' : req.type === 'story' ? 'סטורי' : 'פוסט'}</strong>
                        {req.description && <p>{req.description}</p>}
                      </div>
                    </div>
                  ))}

                  {campaign.minFollowers > 0 && (
                    <div className="requirement-item">
                      <FiUsers className="req-icon" />
                      <div className="req-info">
                        <strong>מינימום {campaign.minFollowers.toLocaleString()} עוקבים</strong>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Guidelines */}
              {(campaign.guidelines || campaign.additionalInfo || campaign.dos?.length > 0 || campaign.donts?.length > 0) && (
                <div className="content-card">
                  <h2>דגשים חשובים</h2>
                  {(campaign.guidelines || campaign.additionalInfo) && (
                    <p className="guidelines-text">{campaign.guidelines || campaign.additionalInfo}</p>
                  )}
                  {(campaign.dos?.length > 0 || campaign.donts?.length > 0) && (
                    <div className="guidelines-grid">
                      {campaign.dos?.length > 0 && (
                        <div className="guidelines-column dos">
                          <h4><FiCheck /> כן לעשות</h4>
                          <ul>
                            {campaign.dos.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {campaign.donts?.length > 0 && (
                        <div className="guidelines-column donts">
                          <h4><FiX /> לא לעשות</h4>
                          <ul>
                            {campaign.donts.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="sidebar">
              {/* Apply Card */}
              <div className="sidebar-card apply-card">
                <h3>הגש מועמדות</h3>

                {/* Compensation */}
                <div className="compensation-section">
                  <h4>תמורה</h4>
                  {campaign.compensation?.map((comp, i) => (
                    <div key={i} className="compensation-item">
                      {comp.type === 'paid' ? <FiDollarSign /> : <FiGift />}
                      <span>
                        {comp.type === 'paid' && comp.value && `₪${comp.value}`}
                        {comp.type === 'product' && 'מוצר'}
                        {comp.type === 'giftcard' && comp.value && `גיפטקארד ₪${comp.value}`}
                        {comp.description && ` - ${comp.description}`}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Platforms */}
                {(campaign.platforms?.length > 0 || campaign.targetPlatforms?.length > 0) && (
                  <div className="platforms-section">
                    <h4>פלטפורמות</h4>
                    <div className="platforms-list">
                      {(campaign.platforms || campaign.targetPlatforms)?.map((platform, i) => {
                        const Icon = getPlatformIcon(platform);
                        const platformNames = {
                          instagram: 'אינסטגרם',
                          tiktok: 'טיקטוק',
                          facebook: 'פייסבוק'
                        };
                        return (
                          <div key={i} className="platform-badge">
                            <Icon />
                            <span>{platformNames[platform] || platform}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Target */}
                {campaign.targetCategories?.length > 0 && (
                  <div className="categories-section">
                    <h4>קטגוריות</h4>
                    <div className="categories-list">
                      {campaign.targetCategories.map((cat, i) => (
                        <span key={i} className="category-badge">{cat}</span>
                      ))}
                    </div>
                  </div>
                )}

                <a
                  href="/CustomerAgreement.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="agreement-link"
                >
                  <FiFileText />
                  צפייה בהסכם שיתוף פעולה
                </a>

                {hasAlreadyApplied ? (
                  <div className="already-applied-notice">
                    <FiCheck />
                    <span>כבר הגשת מועמדות לקמפיין זה</span>
                    {existingApplication?.status && (
                      <span className={`badge badge-${existingApplication.status === 'approved' ? 'success' : existingApplication.status === 'rejected' ? 'error' : 'warning'}`}>
                        {existingApplication.status === 'approved' ? 'אושר' : existingApplication.status === 'rejected' ? 'נדחה' : 'ממתין'}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={handleApply}
                    disabled={!campaign.isOpenForApplications}
                  >
                    {campaign.isOpenForApplications ? 'הגש מועמדות' : 'הרשמה סגורה'}
                  </button>
                )}
              </div>

              {/* Hashtags */}
              {campaign.hashtags?.length > 0 && (
                <div className="sidebar-card">
                  <h4>האשטגים</h4>
                  <div className="hashtags-list">
                    {campaign.hashtags.map((tag, i) => (
                      <span key={i} className="hashtag">#{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Agreement Modal */}
      <AgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        onSubmit={handleAgreementSubmit}
        isLoading={signAgreementMutation.isLoading}
        creatorName={user?.displayName || user?.fullName}
        creatorEmail={user?.email}
      />
    </div>
  );
};

export default CampaignDetailPage;
